import Cookies from "js-cookie";
import { redirect } from "react-router";
import { setAuthorizationHeader } from "../api/clients/graphql-client";
import { ClientError } from "graphql-request";
import { useAppStore } from "./app.provider";
import { ResourceNotFound } from "../exceptions/resource-not-found.exception";
import { getEnv } from "../env";
import {
  clearSandboxMode,
  isSandboxMode,
  SANDBOX_AUTH_COOKIE,
  getSandboxCookieAttributes,
} from "../sandbox/sandbox-context";

export const useAuthenticatedUser = () => {
  const { authenticatedUser } = useAppStore();

  if (!authenticatedUser) {
    throw new ResourceNotFound();
  }

  return { user: authenticatedUser };
};

export const isAuthenticated = (): boolean => {
  return !!getAuthorizationHeader();
};

export const getAuthorizationHeader = (): string | undefined => {
  if (isSandboxMode()) {
    return Cookies.get(SANDBOX_AUTH_COOKIE);
  }
  return Cookies.get("Authorization");
};

export const setAuth = (accessToken: string): void => {
  clearSandboxMode();
  void import("../sandbox/sandbox-provider").then(({ stopSandboxWorker }) =>
    stopSandboxWorker(),
  );

  Cookies.set("Authorization", accessToken, {
    domain: getEnv("AUTH_COOKIE_DOMAIN"),
    secure: true,
  });

  setAuthorizationHeader(accessToken);
};

export const logout = (): void => {
  Cookies.remove("Authorization", {
    domain: getEnv("AUTH_COOKIE_DOMAIN"),
    secure: true,
  });
  Cookies.remove(SANDBOX_AUTH_COOKIE, getSandboxCookieAttributes());
};

export const redirectIfNoCredentials = (from: URL) => {
  const authorization = getAuthorizationHeader();

  if (!authorization) {
    const redirectTo = `${from.pathname}${from.search}`;

    return redirect(
      `/login${redirectTo && redirectTo != "/" ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ""}`,
    );
  }

  setAuthorizationHeader(authorization);
  return null;
};

export const redirectIfCredentials = () => {
  if (isAuthenticated()) {
    return redirect("/");
  }

  return null;
};

export const isAuthError = (error: unknown) => {
  if (error instanceof ClientError) {
    return error.response.errors?.some(
      (error) => error.extensions.code === "UNAUTHENTICATED",
    );
  }

  return false;
};
